// core.js — shared application state and runtime configuration.
//
// As main.js gets broken into focused modules, this is the single "spine"
// they all import from. Two kinds of shared state live here:
//
//   parameters — the tunable knobs the UI mutates in place (never reassigned),
//                so every module that imports it sees the same live object.
//
//   app        — runtime singletons that *do* get reassigned (the current tree,
//                the scene, etc.). Because ES module imports are read-only
//                bindings, reassignable state has to live on an object so other
//                modules observe the updated value rather than a stale snapshot.
//                main (main.js) populates these fields during init and registers
//                callbacks (e.g. createNewTreeWithData) so feature modules can
//                trigger core behavior without importing core's internals.

import * as THREE from 'three';

// Tunable parameters — mutated in place by the UI controls; never reassigned.
export const parameters = {
  truncHeight: 86,
  truncThickness: 9,
  truncNoise: 4,
  truncCurve: 6,
  truncColor: 0x8B4513,
  leafType: "pine",
  foliageColor: "greens",
  foliageDensity: 18,
  foliageSize: 15,
  animationSpeed: 2.5,
  leafClumpiness: 0.6,
  windStrength: 0.05,
  windDirection: new THREE.Vector3(0.5, 0, 0.5).normalize(),
  shapeMode: false,
  // Camera-relative bend gain (interaction.js shapeBranch). 1.0 ≈ branch tip
  // follows the cursor 1:1, scaled per branch type. Lower = stiffer bends.
  shapeSensitivity: 1.0
};

// Pristine snapshot of the defaults in their *serialized* shape (windDirection
// flattened to x/y/z), captured here before any UI mutation. shareBonsai diffs a
// tree's params against this so a share link carries only what differs from
// default; the recipient re-merges over this to rebuild the full set.
export const PARAMETER_DEFAULTS = Object.freeze({
  ...parameters,
  windDirection: {
    x: parameters.windDirection.x,
    y: parameters.windDirection.y,
    z: parameters.windDirection.z
  }
});

// Foliage/bark color palettes. Pure data + a random-pick helper. Used by the
// Tree/Branch core; kept here so it (and Math2) have a single shared home.
export const Colors = {
  greens: [0x228B22, 0x16A224, 0x217215, 0x2E9F41, 0x045104],
  spring: [0x9ACD32, 0x7CB518, 0xB5E61D, 0x6DBE45, 0xCFE65A],
  sage:   [0x8FBC8F, 0x6B8E6B, 0x5F9EA0, 0x789F8A, 0x9CAF88],
  // Soft sakura pinks + whites — the one pink in the app: the "Pink" leaf option
  // and the cherry-blossom default both draw from this (there's no separate
  // harsher pink palette).
  pinks: [0xFFB7C5, 0xFFC8D6, 0xFFDCE5, 0xFFFFFF, 0xF6BFD0],
  yellows: [0xFFD700, 0xFFFF00, 0xFFA500, 0xFFE135, 0xF0E68C],
  // Burgundy foliage (Japanese maple / copper beech): wine-reds and maroons, no
  // blue-purple. Key stays `purples` so saved trees and the <option> value still
  // resolve; only the colors and the UI label changed.
  purples: [0x7A1F2B, 0x5C1622, 0x9B2D3B, 0xAE4055, 0x6E2433],
  autumn: [0xFF4500, 0xFF6347, 0xFFD700, 0xDC143C, 0xFF8C00],
  pine: [0x1B4D3E, 0x0F3B24, 0x2D5016, 0x1E4B26, 0x234D1A],
  // Shimpaku juniper scale foliage: a softer, slightly blue-grey green than pine —
  // the cool, dusty sage-teal of mature juniper "cloud" pads.
  juniper: [0x4A6B4C, 0x3C5C44, 0x5B7A58, 0x6E8C66, 0x42654E],

  // Routed through the seeded stream so a tree's foliage colors are reproduced
  // exactly on replay (see Math2 below).
  getRandomFrom: function(palette) {
    const colors = this[palette] || this.greens;
    return colors[Math.floor(Math2.random() * colors.length)];
  }
};

// Seeded RNG + small random-range helpers.
//
// DETERMINISM INVARIANT: everything that affects a tree's *geometry/structure*
// (trunk noise/lean, branch angles/heights, foliage placement, nebari, palette
// picks) must draw from Math2.random() — the seeded stream — NOT Math.random().
// A tree is regenerated faithfully by re-seeding with its stored seed and
// replaying the same structural method calls in the same order, which only works
// if the stream is consumed in an identical sequence both times. Purely cosmetic
// randomness that does NOT change geometry (gsap durations/delays, static scene
// dressing in environment.js, the gallery's tree-name picker) must stay on plain
// Math.random() so it never perturbs the seeded sequence.
//
// mulberry32: tiny, fast, deterministic 32-bit PRNG. _state advances on each draw.
let _rngState = (Math.random() * 0xffffffff) >>> 0; // randomized until a tree seeds it
export const Math2 = {
  // Set the current stream state. Mask to uint32 and avoid the degenerate 0 seed.
  seed: (n) => { _rngState = (n >>> 0) || 0x9e3779b9; },
  // Seeded replacement for Math.random(); returns a float in [0, 1).
  random: () => {
    _rngState = (_rngState + 0x6D2B79F5) >>> 0;
    let t = _rngState;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  },
  rangeRandom: (min, max) => min + Math2.random() * (max - min),
  rangeRandomInt: (min, max) => Math.floor(Math2.rangeRandom(min, max))
};

// Shared runtime singletons. Assigned by main.js; read by feature modules.
export const app = {
  scene: null,                 // the main THREE.Scene (set once in init)
  camera: null,                // the PerspectiveCamera (set once in init)
  renderer: null,              // the WebGLRenderer (set once in init)
  controls: null,              // OrbitControls (set once in init)
  tree: null,                  // the current Tree instance (reassigned on regen)
  createNewTreeWithData: null, // (treeData) => void, registered by main.js
  currentTreeId: null,         // id of the saved tree on screen (null = unsaved/new)
  newTreeIdentity: null        // () => void, registered by gallery.js (resets name/id)
};
